import { Injectable } from '@angular/core';
import { IFaq } from '@shared/interfaces';
import { HttpClient } from '@angular/common/http';
import { ApiService } from '@shared/services/api.service';
import { BehaviorSubject } from 'rxjs';
import { FileService } from '@shared/services/file.service';
import { IAttachment } from '@shared/interfaces/mailing';

@Injectable({
    providedIn: 'root'
})
export class FaqService {
    faqItems: IFaq[];
    faqController = new BehaviorSubject<boolean>(false);

    constructor(
        private apiService: ApiService,
        private fileService: FileService
    ) {}

    getFAQ(): Promise<IFaq[]> {
        if (this.faqItems?.length) {
            return Promise.resolve(this.faqItems);
        }
        return this.apiService.get('/faqs').then(e => {
            this.faqItems = e;
            return this.faqItems;
        });
    }

    getAll(): Promise<IFaq[]> {
        return this.apiService.get(`/faqs`);
    }

    getAllForAdmin(): Promise<IFaq[]> {
        return this.apiService.get(`/faqs/admin`);
    }

    getItem(id: string): Promise<IFaq> {
        return this.apiService.get(`/faqs?id=${encodeURIComponent(id)}`);
    }

    createFaq(item: IFaq): Promise<IFaq> {
        return this.apiService.post('/faqs', item);
    }

    changeState(item: IFaq): Promise<IFaq[]> {
        return this.apiService.patch('/faqs/state', item);
    }

    updateItem(item: IFaq): Promise<IFaq[]> {
        return this.apiService.patch('/faqs', item);
    }

    saveSort(item: IFaq): Promise<IFaq[]> {
        return this.apiService.patch('/faqs/sort', {
            items: item.items.map(e => ({
                id: e.id,
                sortIndex: e.sortIndex
            }))
        });
    }

    deleteFaq(id: string): Promise<number> {
        return this.apiService.delete(`/faqs/${encodeURIComponent(id)}`);
    }

    toggleFaq() {
        this.faqController.next(!this.faqController.value);
    }

    public async gatherDocuments(item: IFaq) {
        return await new Promise<void>(resolve => {
            if (item.documents && item.documents.length) {
                const documents = item.documents.filter(e => e instanceof File);
                if (documents.length) {
                    const reader = new FileReader();
                    reader.readAsArrayBuffer(documents[0] as any as File);
                    reader.onload = () => {
                        item.documents = item.documents.filter(e => e.name !== (documents[0] as any as File).name);
                        item.documents.push({
                            name: (documents[0] as any as File).name,
                            body: reader.result as any,
                            size: (documents[0] as any as File).size
                        });
                        resolve();
                    };
                } else {
                    resolve();
                }
            } else {
                resolve();
            }
        });
    }

    public async gatherDocument(item: IAttachment[]): Promise<IAttachment[]> {
        return await new Promise<IAttachment[]>(resolve => {
            if (item && item.length) {
                const documents = item.filter(e => e instanceof File);
                if (documents.length) {
                    const reader = new FileReader();
                    reader.readAsArrayBuffer(documents[0] as any as File);
                    reader.onload = () => {
                        item = item.filter(e => e.name !== (documents[0] as any as File).name);
                        item.push({
                            name: (documents[0] as any as File).name,
                            body: reader.result as any,
                            size: (documents[0] as any as File).size
                        });
                        resolve(item);
                    };
                } else {
                    resolve([]);
                }
            } else {
                resolve([]);
            }
        });
    }

    async handleDocuments(item: IFaq) {
        console.log(item);
        await this.gatherDocuments(item);
        item.documents = item.documents?.filter(e => !!e.name) ?? [];

        const newDocuments = item.documents?.filter((e: IAttachment) => !!e.body);
        if (newDocuments?.length) {
            const uploads = await Promise.all(
                newDocuments.map(async (e: IAttachment) => {
                    const uploadLink = await this.fileService.getUploadLinkFaq(item.id, e.name);
                    return { uploadLink, body: e.body };
                })
            );

            await Promise.all(uploads.map(e => this.fileService.uploadAttachment(e.uploadLink?.link, e.body)));

            for (const document of item.documents as IAttachment[]) {
                document.body = null;
            }
        }
    }

    async handleDocument(id: string, attachments: IAttachment[], lang?: string) {
        attachments = await this.gatherDocument(attachments);
        attachments = attachments.filter(a => !!a.name) ?? [];
        const newDocuments = attachments?.filter((attachment: IAttachment) => !!attachment.body);
        if (newDocuments?.length) {
            const uploads = await Promise.all(
                newDocuments.map(async (attachment: IAttachment) => {
                    const uploadLink = await this.fileService.getUploadLinkFaq(id, attachment.name, lang);
                    return { uploadLink, body: attachment.body };
                })
            );
            await Promise.all(
                uploads.map(upload => this.fileService.uploadAttachment(upload.uploadLink?.link, upload.body))
            );

            for (const attachment of attachments as IAttachment[]) {
                attachment.body = null;
            }

            return attachments;
        }
    }
}
